import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { getAuthErrorMessage, getAuthRedirectUrl, normalizeEmail } from "../utils/auth";

export default function OwnerAccessPage({ initialError = "" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [error, setError] = useState(initialError);
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);

  async function signIn(event) {
    event.preventDefault();
    if (!supabase || loadingAction) return;
    setLoadingAction("login");
    setError("");
    setInfo("");

    try {
      const normalized = normalizeEmail(email);
      if (!normalized) throw new Error("Bitte die Owner-E-Mail eintragen.");
      if (!password) throw new Error("Bitte das Passwort eintragen.");

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: normalized,
        password,
      });
      if (signInError) throw signInError;
      if (!data?.session?.access_token) {
        throw new Error("Die Anmeldung wurde nicht als gültige Session bestätigt.");
      }

      setInfo("Passwort bestätigt. Owner-Berechtigung wird geprüft …");
    } catch (loginError) {
      setError(getAuthErrorMessage(loginError));
    } finally {
      setLoadingAction(null);
    }
  }

  async function resetPassword() {
    if (!supabase || loadingAction) return;
    setLoadingAction("reset");
    setError("");
    setInfo("");

    try {
      const normalized = normalizeEmail(email);
      if (!normalized) throw new Error("Bitte zuerst die Owner-E-Mail eintragen.");
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalized, {
        redirectTo: `${getAuthRedirectUrl().replace(/\/$/, "")}/admin`,
      });
      if (resetError) throw resetError;
      setInfo("Reset-Link wurde an die hinterlegte E-Mail-Adresse gesendet.");
    } catch (resetError) {
      setError(getAuthErrorMessage(resetError));
    } finally {
      setLoadingAction(null);
    }
  }

  return (
    <div className="min-h-screen bg-[#020711] px-5 py-8 text-slate-100">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-5xl items-center justify-center">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-cyan-300/15 bg-[#07111f]/90 shadow-[0_40px_120px_rgba(0,0,0,.45)] backdrop-blur-2xl lg:grid-cols-[1.05fr_.95fr]">
          <section className="relative overflow-hidden border-b border-white/5 p-8 md:p-12 lg:border-b-0 lg:border-r">
            <div className="pointer-events-none absolute -left-20 top-10 h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 right-0 h-72 w-72 rounded-full bg-violet-500/10 blur-3xl" />

            <a
              href="/"
              className="relative inline-flex items-center gap-2 text-xs font-semibold text-slate-400 transition hover:text-cyan-200"
            >
              <ArrowLeft size={15} />
              Zur öffentlichen HQS Website
            </a>

            <div className="relative mt-16 max-w-md">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/15 bg-cyan-400/5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.15em] text-cyan-200">
                <ShieldCheck size={13} />
                Geschützter Owner-Bereich
              </div>
              <h1 className="mt-5 text-4xl font-black tracking-[-.045em] text-white md:text-5xl">
                HQS NEXUS
              </h1>
              <p className="mt-4 text-sm leading-7 text-slate-400">
                Der interne Bereich enthält Forecast-Rohdaten, Portfolio-, Agenten-, Pipeline-,
                Provider- und Governance-Informationen. Er ist ausschließlich für den
                hinterlegten HQS Owner bestimmt.
              </p>

              <div className="mt-10 grid gap-3 text-xs text-slate-400">
                <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[.025] px-4 py-3">
                  <LockKeyhole size={16} className="text-cyan-300" />
                  Persönliche Anmeldung mit geschütztem Zugriff
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[.025] px-4 py-3">
                  <ShieldCheck size={16} className="text-emerald-300" />
                  Nicht autorisierte Accounts erhalten keinen NEXUS-Zugriff
                </div>
              </div>
            </div>
          </section>

          <section className="p-8 md:p-12">
            <div className="mx-auto max-w-sm">
              <div className="mb-8">
                <p className="text-[10px] font-bold uppercase tracking-[.16em] text-slate-500">
                  Dein persönlicher Arbeitsplatz
                </p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight text-white">Admin-Zugang</h2>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  Melde dich mit deiner Owner-E-Mail und deinem Passwort an.
                </p>
              </div>

              <form onSubmit={signIn} className="space-y-4" aria-busy={loadingAction !== null}>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-300">E-Mail</span>
                  <div className="flex items-center rounded-xl border border-slate-700 bg-slate-950/60 px-3 focus-within:border-cyan-400/70">
                    <Mail size={16} className="text-slate-500" />
                    <input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="w-full bg-transparent px-3 py-3.5 text-sm text-white outline-none placeholder:text-slate-600"
                      placeholder="Owner E-Mail"
                      required
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-300">Passwort</span>
                  <div className="flex items-center rounded-xl border border-slate-700 bg-slate-950/60 px-3 focus-within:border-cyan-400/70">
                    <KeyRound size={16} className="text-slate-500" />
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full bg-transparent px-3 py-3.5 text-sm text-white outline-none placeholder:text-slate-600"
                      placeholder="••••••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="rounded-lg p-1 text-slate-500 transition hover:text-cyan-200"
                      aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </label>

                {error && (
                  <div role="alert" className="rounded-xl border border-rose-500/20 bg-rose-500/8 p-3 text-xs leading-5 text-rose-200">
                    {error}
                  </div>
                )}
                {info && (
                  <div role="status" className="rounded-xl border border-emerald-500/20 bg-emerald-500/8 p-3 text-xs leading-5 text-emerald-200">
                    {info}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loadingAction !== null}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-400 px-4 font-bold text-slate-950 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingAction === "login" ? <Loader2 size={17} className="animate-spin" /> : <LockKeyhole size={16} />}
                  {loadingAction === "login" ? "Anmeldung läuft …" : "Owner-Bereich öffnen"}
                </button>
              </form>

              <button
                type="button"
                onClick={resetPassword}
                disabled={loadingAction !== null}
                className="mt-4 w-full text-center text-xs font-medium text-slate-500 transition hover:text-slate-300 disabled:opacity-50"
              >
                Passwort vergessen oder neu setzen
              </button>

              <p className="mt-8 border-t border-white/5 pt-5 text-center text-[10px] leading-5 text-slate-600">
                Zugang nur für den freigegebenen Owner. Deine Berechtigung wird bei jeder Anmeldung geprüft.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
